'use client';
import { useState, useRef } from 'react';
import parse from 'html-react-parser';
import { AnimateWrapper } from '@overlapdev/wrappers';
import { Button } from '@overlapdev/elements';
import { ContentType, LinkType } from '@overlapdev/types';
import { CircleArrowSVG, PlusSVG, MinusSVG } from 'components/svg';

type ItemType = {
	body_content?: string;
	title: string;
};

type DataType = {
	bottom_cta?: {
		background_color?: string;
		body_content?: string;
		link?: LinkType;
		style: string;
		text_color?: string;
	};
	content?: ContentType;
	custom_class?: string;
	items?: ItemType[];
	sec_anchor_name?: string;
};

const AccordionContent = ({ data }: { data: DataType }) => {
	//__Variables
	const { bottom_cta, content, custom_class, items, sec_anchor_name } = data || {};
	const { body_content, heading, heading_tag, subheading } = content || {};

	//__State
	const [open, setOpen] = useState<number[]>([]);

	//__Refs
	const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

	//__Functions
	const updateOpen = (index: number) => {
		let open_copy = [...open];
		if (open_copy.includes(index)) {
			open_copy = open_copy.filter((e) => e !== index);
		} else {
			open_copy.push(index);
		}
		setOpen(open_copy);
	};

	//__Styles
	const inlineStyles = {
		bottom_cta: {
			backgroundColor: bottom_cta?.background_color || '#373737',
			color: bottom_cta?.text_color || '#fff'
		}
	};

	if (!data || !items?.length) return null;
	return (
		<section className={`gutter-x my-14 lg:my-32 ${custom_class ? ` ${custom_class}` : ''}`} id={sec_anchor_name}>
			<div className='max-w-202 mx-auto flex flex-col gap-10 md:gap-20'>
				<div className='flex flex-col gap-5'>
					{subheading ? (
						<AnimateWrapper animate_type='fadeup' element='h6' className='mb-5 max-md:gutter-x'>
							{parse(subheading)}
						</AnimateWrapper>
					) : null}
					{heading ? (
						<AnimateWrapper animate_type='fadeup' element={heading_tag || 'div'} className='text-black'>
							{parse(heading)}
						</AnimateWrapper>
					) : null}
					{body_content ? (
						<AnimateWrapper animate_type='fadeup' element='div' threshold={0} className='font-semibold'>
							{parse(body_content)}
						</AnimateWrapper>
					) : null}
				</div>
				<div className='flex flex-col gap-7'>
					{items.map((item: ItemType, index: number) => {
						const { body_content, title } = item;
						const isOpen = open.includes(index);
						const ref = contentRefs.current[index];
						const maxHeight = isOpen && ref ? `${ref.scrollHeight}px` : '0px';

						return (
							<AnimateWrapper key={`accordion_${index}`} animate_type='fadeup' element='div'>
								<div className='cursor-pointer flex gap-2 items-start justify-between group' onClick={() => updateOpen(index)}>
									<h6 className='text-black'>{parse(title)}</h6>
									<div className='opacity-60 transition-ease md:mt-1 group-hover:opacity-100'>{isOpen ? <MinusSVG width={24} /> : <PlusSVG width={24} />}</div>
								</div>
								{body_content ? (
									<div
										ref={(el) => {
											contentRefs.current[index] = el;
										}}
										className='overflow-hidden transition-ease'
										style={{
											maxHeight
										}}
									>
										<div className='pt-4'>{parse(body_content)}</div>
									</div>
								) : null}
							</AnimateWrapper>
						);
					})}
				</div>
				{bottom_cta?.body_content || bottom_cta?.link?.url ? (
					<AnimateWrapper animate_type='fadeup' element='div' className='flex gap-8 items-center justify-between p-10 rounded-3xl text-white max-md:flex-col max-md:items-start' style={inlineStyles.bottom_cta}>
						{bottom_cta?.body_content ? <div className='flex flex-col gap-4 [&_a]:text-inherit [&_a]:underline [&_a]:hover:no-underline'>{parse(bottom_cta.body_content)}</div> : null}
						{bottom_cta?.link?.url ? (
							<Button link={bottom_cta.link} className={`btn ${bottom_cta.style}`}>
								<CircleArrowSVG width={18} />
							</Button>
						) : null}
					</AnimateWrapper>
				) : null}
			</div>
		</section>
	);
};

export default AccordionContent;
